import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { type To } from 'react-router';
import styled, { keyframes } from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { roundLastChild } from '@sorare/core/src/style/utils';

import { isGameScheduled } from '@sorare/marketplace/src/shared/game/lib';

import { CompetitionName } from './CompetitionName';
import { GameTeamsMatchup } from './GameTeamsMatchup';
import { GameWinOddsProjection, validStat } from './GameWinOddsProjection';
import type { Game_anyGameInterface } from './__generated__/index.graphql';

const overlayOpacity = keyframes`
    from {
      --overlay-opacity: 85%;
    }
    to {
      --overlay-opacity: 70%;
    }
`;

const GameRoot = styled(Vertical).attrs({ gap: 2 })`
  width: 100%;
  position: relative;
  padding: var(--double-unit);
  border-radius: var(--triple-unit);
  overflow: hidden;
  border: 1px solid var(--c-nd-150-opaque);
  flex-shrink: 0;
  z-index: 0;

  --overlay-opacity: 85%;
  @property --overlay-opacity {
    syntax: '<percentage>';
    initial-value: 85%;
    inherits: false;
  }

  background: linear-gradient(
      rgba(0, 0, 0, var(--overlay-opacity)),
      rgba(0, 0, 0, var(--overlay-opacity))
    ),
    conic-gradient(
      from 180deg at 50% 50%,
      #000000 0deg,
      var(--c-home-team-color, #241f20) 90.12deg,
      #000000 180deg,
      var(--c-away-team-color, #241f20) 269.88deg,
      #000000 360deg
    );

  &::after {
    content: '';
    border-radius: var(--triple-unit);
    position: absolute;
    inset: 0;
    backdrop-filter: blur(16px);
    z-index: -1;
  }

  &.selected {
    border: 1px solid var(--c-blue-400);
  }

  &:hover,
  &.selected {
    animation: ${overlayOpacity} 0.2s forwards;
  }
`;
const SpaceBetweenHorizontal = styled(SBHorizontal)`
  min-height: var(--triple-unit);
  min-width: 0;
`;
const StyledButtonBase = styled(LinkOverlay).attrs({ as: ButtonBase })`
  width: 100%;
  position: absolute;
  inset: 0;
  z-index: 1;
`;
const GameWinOddsProjectionWrapper = styled.div`
  height: 18px;
  border-radius: var(--half-unit);

  ${roundLastChild}
`;

type Props = {
  game: Game_anyGameInterface;
  topRight?: ReactNode;
  children?: ReactNode;
  selected?: boolean;
  to?: To;
  onClick?: () => void;
} & Pick<React.ComponentProps<typeof GameTeamsMatchup>, 'displayDay'>;
export const Game = ({
  game,
  topRight,
  children,
  selected,
  to,
  onClick,
  displayDay,
}: Props) => {
  const isScheduled = isGameScheduled(game.status);

  const displayGameOdds =
    validStat(game.homeStats) && validStat(game.awayStats);

  return (
    <LinkBox>
      <GameRoot
        className={classNames({ selected })}
        style={
          {
            '--c-home-team-color': game.homeTeam?.styleGuideColor,
            '--c-away-team-color': game.awayTeam?.styleGuideColor,
          } as CSSProperties
        }
      >
        {!!(to || onClick) && (
          <StyledButtonBase
            as={ButtonBase}
            to={to}
            onClick={onClick}
            keepSearchParams
          />
        )}
        {!!topRight && (
          <SpaceBetweenHorizontal gap={1}>
            <CompetitionName displayName={game.competition.displayName} />
            {topRight}
          </SpaceBetweenHorizontal>
        )}
        <GameTeamsMatchup game={game} displayDay={displayDay}>
          {!!isScheduled && !!displayGameOdds && (
            <GameWinOddsProjectionWrapper>
              <GameWinOddsProjection
                homeTeamGameStatsInterface={game.homeStats}
                awayTeamGameStatsInterface={game.awayStats}
              />
            </GameWinOddsProjectionWrapper>
          )}
          {children}
        </GameTeamsMatchup>
      </GameRoot>
    </LinkBox>
  );
};

Game.fragments = {
  anyGameInterface: gql`
    fragment Game_anyGameInterface on AnyGameInterface {
      id
      status: statusTyped
      competition {
        slug
        displayName
      }
      homeTeam {
        slug
        styleGuideColor
      }
      awayTeam {
        slug
        styleGuideColor
      }
      homeStats {
        id
        ...GameWinOddsProjection_teamGameStatsInterface
      }
      awayStats {
        id
        ...GameWinOddsProjection_teamGameStatsInterface
      }
      ...GameTeamsMatchup_anyGameInterface
    }
    ${GameTeamsMatchup.fragments.anyGameInterface}
    ${GameWinOddsProjection.fragments.teamGameStatsInterface}
  ` as TypedDocumentNode<Game_anyGameInterface>,
};
